import { Component, inject, signal } from '@angular/core';
import { Session } from '../../../../../services/session';
import { SchoolService } from '../../../../../services/school';
import { FlashService } from '../../../../../services/flash';
import { ISchools } from '../../../../../interfaces/school';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { Helpers } from '../../../../../utilities/helpers';
import { ActionButton } from '../../../../../shared/action-button/action-button';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { NotFoundError } from '../../../../../shared/not-found-error/not-found-error';
import { Pagination } from '../../../../../shared/pagination/pagination';
import { NgClass } from '@angular/common';
import { ILevelSubjects, ISubject, ISubjects } from '../../../../../interfaces/subject';
import { SubjectService } from '../../../../../services/subject';
import { ClassService } from '../../../../../services/class';
import { TeacherService } from '../../../../../services/teacher';
import { TeacherClassSubjectService } from '../../../../../services/teacher-class-subject';
import { IClasses, ITeacherClassSubjects } from '../../../../../interfaces/class';
import { ITeacher, ITeachers } from '../../../../../interfaces/teacher';
import { LevelService } from '../../../../../services/level';
import { ILevels } from '../../../../../interfaces/level';
import { forkJoin } from 'rxjs';
import { AcademicYearService } from '../../../../../services/academic-year';
import { IAcademicYears } from '../../../../../interfaces/academic-year';

@Component({
  selector: 'app-subjects',
  imports: [
    ActionButton,
    LoadingIndicator,
    NotFoundError,
    Pagination,
    ReactiveFormsModule,
    NgClass,
    FormsModule,
  ],
  templateUrl: './subjects.html',
  styleUrl: './subjects.css',
})
export class Subjects {
  constructor(
    protected subjectService: SubjectService,
    private session: Session,
    private schoolService: SchoolService,
    private classService: ClassService,
    private teacherService: TeacherService,
    private teacherClassSubjectService: TeacherClassSubjectService,
    private levelService: LevelService,
    protected academicYearService: AcademicYearService,
  ) {
    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (this.loggedUserRole === 'SUPER_ADMIN') {
      this.getSchools();
    }

    this.getSubjects();
    this.getAcademicYears();
  }

  protected subjects = signal<ISubjects | null>(null);
  protected readonly flashService = inject(FlashService);
  protected loggedUserRole?: string;
  protected schoolId?: string;
  protected pagination = signal({ page: 0, size: 10 });
  protected readonly Helpers = Helpers;
  protected schools = signal<ISchools | null>(null);
  protected currentFilterType = '';
  protected currentFilterValue = '';
  protected search: string = '';
  protected filterSchoolId: string = '';
  protected status: string = '';
  protected isFiltering = signal<boolean>(false);
  protected changeStatus = signal<boolean>(false);
  protected editSubject = signal<boolean>(false);
  protected isEditing = signal<boolean>(false);
  protected validStatuses = ['ACTIVE', 'INACTIVE', 'ARCHIVED'];
  protected nameOfSubject!: string;
  protected subjectCode!: string;
  protected subjectDescription!: string;
  protected admins = ['SUPER_ADMIN', 'SCHOOL_ADMIN'];
  protected showDetail = signal<boolean>(false);
  protected selectedSubject: ISubject['payload'] = {
    id: '',
    public_id: '',
    school_id: '',
    school_name: '',
    name: '',
    code: '',
    description: '',
    status: '',
    created_at: '',
    updated_at: '',
  };
  protected readonly Number = Number;
  protected levelSubjects = signal<ILevelSubjects | null>(null);
  protected teacherClassSubjects = signal<ITeacherClassSubjects | null>(null);

  protected assignClassSubject = signal<boolean>(false);
  protected assignSubjectTeacherCheck = signal<boolean>(false);
  protected isAssigning = signal<boolean>(false);
  protected classList = signal<IClasses | null>(null);
  protected teachers = signal<ITeachers | null>(null);
  protected selectedClassId: string = '';
  protected classTeacherId: string = '';
  protected teacherName: string = '';
  protected teacherPublicId: string = '';
  protected assignmentRole: string = '';
  protected query: string = '';
  protected invalidStatuses = ['INACTIVE', 'ARCHIVED', 'ON_LEAVE', 'TERMINATED'];
  protected levels = signal<ILevels | null>(null);
  protected selectedLevels: string[] = [];
  protected assignLevelPopup = signal<boolean>(false);
  protected academicYears = signal<IAcademicYears | null>(null);
  protected selectedAcademicYearId: string = '';

  protected getSubjects(
    filterType = this.currentFilterType,
    value = this.currentFilterValue,
  ): void {
    this.isFiltering.set(true);

    this.currentFilterType = filterType;
    this.currentFilterValue = value;

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
    };

    if (filterType && value) {
      params[filterType] = value;
    }
    this.subjectService.getSubjects(`${ApiEndpoints.url}/subject`, params).subscribe({
      next: (res) => {
        this.subjects.set(res);
        this.isFiltering.set(false);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load Subjects. Refresh the page.',
        );
      },
    });
  }

  protected getSchools(): void {
    this.schoolService.getSchools(`${ApiEndpoints.url}/school`).subscribe({
      next: (data) => {
        this.schools.set(data);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error loading schools.');
      },
    });
  }

  protected loadPage(page: number): void {
    this.pagination.update((p) => ({
      ...p,
      page,
    }));

    this.getSubjects();
  }

  protected selectSubject(action: string, payload: ISubject['payload']): void {
    if (action === 'status') {
      this.changeStatus.set(true);
    } else if (action === 'edit') {
      this.editSubject.set(true);
    } else {
      this.getLevelSubjects(payload.id);
      this.getTeacherClassSubjects(payload.id);
      this.showDetail.set(true);
    }

    this.selectedSubject = payload;
    this.nameOfSubject = payload.name;
    this.subjectCode = payload.code;
    this.subjectDescription = payload.description;
  }

  protected getLevelSubjects(subjectId: string): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 50,
      subject_id: subjectId,
    };

    this.subjectService.getLevelSubjects(`${ApiEndpoints.url}/level-subject`, params).subscribe({
      next: (res) => {
        this.levelSubjects.set(res);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load subjects. Refresh the page',
        );
      },
    });
  }

  protected getTeacherClassSubjects(subjectId: string): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 50,
      subject_id: subjectId,
    };

    this.subjectService
      .getTeacherClassSubjects(`${ApiEndpoints.url}/teacher-class-subject`, params)
      .subscribe({
        next: (res) => {
          this.teacherClassSubjects.set(res);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load subjects for teachers. Refresh the page',
          );
        },
      });
  }

  protected openAssignSubjectToTeacher(): void {
    this.selectedAcademicYearId = '';
    this.selectedClassId = '';
    this.getClassesForLevels();
    this.assignSubjectTeacherCheck.set(true);
  }

  protected openAssignSubjectToLevels(): void {
    this.getLevels();
    this.assignLevelPopup.set(true);
  }

  protected getLevels(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 100,
    };

    this.levelService.getLevels(`${ApiEndpoints.url}/level`, params).subscribe({
      next: (ref) => {
        this.levels.set(ref);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Could not load levels. Refresh the page');
      },
    });
  }

  protected selectLevel(levelId: string): void {
    if (this.selectedLevels.includes(levelId)) {
      this.selectedLevels = this.selectedLevels.filter((id) => id !== levelId);

      return;
    }

    this.selectedLevels = [...this.selectedLevels, levelId];
  }

  protected get areAllLevelsSelected(): boolean {
    const allLevels = this.levels()?.payload ?? [];
    return (
      allLevels.length > 0 && allLevels.every((level) => this.selectedLevels.includes(level.id))
    );
  }

  protected selectAllLevels(): void {
    const allLevels = this.levels()?.payload ?? [];

    if (this.areAllLevelsSelected) {
      this.selectedLevels = [];
    } else {
      this.selectedLevels = allLevels.map((level) => level.id);
    }
  }

  protected assignSubjectToLevels(): void {
    if (!this.selectedLevels.length) {
      this.flashService.failure('Select at least one level');
      return;
    }

    if (!this.schoolId && this.loggedUserRole === 'SUPER_ADMIN') {
      this.flashService.failure('School ID required');
      return;
    }

    if (!this.schoolId) {
      this.flashService.failure('Error assigning Subject. Try again.');
      return;
    }

    this.isAssigning.set(true);

    const requests = this.selectedLevels.map((levelId) =>
      this.subjectService.createSubject(`${ApiEndpoints.url}/level-subject`, {
        school_id: this.schoolId,
        level_id: levelId,
        subject_id: this.selectedSubject.id,
      }),
    );

    forkJoin(requests).subscribe({
      next: () => {
        this.isAssigning.set(false);
        this.assignLevelPopup.set(false);
        this.selectedLevels = [];
        this.flashService.success('Subject assigned to levels successfully.');
        this.getLevelSubjects(this.selectedSubject.id);
      },
      error: (err) => {
        this.isAssigning.set(false);
        this.flashService.failure(
          err?.error?.message ?? 'Error assigning Subject to levels. Try again.',
        );
      },
    });
  }

  protected getClassesForLevels(): void {
    const subjectLevelIds = new Set(this.levelSubjects()?.payload?.map((ls) => ls.level_id) ?? []);

    if (!subjectLevelIds.size) {
      this.flashService.failure('No levels have this subject assigned yet.');
      return;
    }

    const params: Record<string, string | number> = {
      page: 0,
      size: 100,
      academic_year_id: this.selectedAcademicYearId,
    };

    this.classService.getClasses(`${ApiEndpoints.url}/class`, params).subscribe({
      next: (res) => {
        const filtered = {
          ...res,
          payload: res.payload.filter((c) => subjectLevelIds.has(c.level_id)),
        };
        this.classList.set(filtered);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load classes. Refresh the page',
        );
      },
    });
  }

  protected onAcademicYearChange(): void {
    this.selectedClassId = '';
    this.getClassesForLevels();
  }

  protected getAcademicYears(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 30,
    };

    this.academicYearService
      .getAcademicYears(`${ApiEndpoints.url}/academic-year`, params)
      .subscribe({
        next: (res) => {
          this.academicYears.set(res);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Academic Years. Refresh the page.',
          );
        },
      });
  }

  protected getClasses(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 50,
    };

    this.classService.getClasses(`${ApiEndpoints.url}/class`, params).subscribe({
      next: (res) => {
        this.classList.set(res);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load classes. Refresh the page',
        );
      },
    });
  }

  protected getTeachers(query: string): void {
    this.teacherService.getTeachers(`${ApiEndpoints.url}/teacher`, { search: query }).subscribe({
      next: (res) => {
        this.teachers.set(res);
        this.teacherName = '';
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load teachers. Refresh the page.',
        );
      },
    });
  }

  protected selectTeacher(teacher: ITeacher['payload']) {
    this.query = '';
    this.teacherName = Helpers.fullName({
      first_name: teacher.first_name,
      last_name: teacher.last_name,
    });
    this.teacherPublicId = teacher.public_id;
    this.classTeacherId = teacher.id;
  }

  protected getLevelSubjectForClass(): string | null {
    const selectedClass = this.classList()?.payload?.find((c) => c.id === this.selectedClassId);

    if (!selectedClass) return null;

    const levelSubject = this.levelSubjects()?.payload?.find(
      (ls) => ls.level_id === selectedClass.level_id && ls.subject_id === this.selectedSubject.id,
    );

    return levelSubject?.id ?? null;
  }

  protected assignSubjectToTeacherFn(form: NgForm): void {
    if (form.invalid || !this.classTeacherId) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.schoolId && this.loggedUserRole === 'SUPER_ADMIN') {
      this.flashService.failure('School ID required');
      return;
    }

    if (!this.schoolId) {
      this.flashService.failure('Error assigning Teacher. Try again.');
      return;
    }

    const levelSubjectId = this.getLevelSubjectForClass();

    if (!levelSubjectId) {
      this.flashService.failure(
        'Subject is not assigned to this class level. Assign subject to class first.',
      );
      return;
    }

    this.isAssigning.set(true);

    const teacherSubjectForm = {
      school_id: this.schoolId,
      teacher_id: this.classTeacherId,
      level_subject_id: levelSubjectId,
      assignment_role: this.assignmentRole,
      class_id: this.selectedClassId,
    };

    this.teacherClassSubjectService
      .createTeacherClassSubject(`${ApiEndpoints.url}/teacher-class-subject`, teacherSubjectForm)
      .subscribe({
        next: () => {
          this.classTeacherId = '';
          this.teacherName = '';
          this.teacherPublicId = '';
          this.assignmentRole = '';
          this.selectedClassId = '';
          this.selectedAcademicYearId = '';
          this.isAssigning.set(false);
          this.assignSubjectTeacherCheck.set(false);
          this.flashService.success('Teacher assigned to subject successfully.');
          this.getTeacherClassSubjects(this.selectedSubject.id);
        },
        error: (err) => {
          this.isAssigning.set(false);
          this.flashService.failure(err?.error?.message ?? 'Error assigning Teacher. Try again.');
        },
      });
  }

  protected editAcademicYearFn(form: NgForm, action: 'status' | 'edit'): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    const payload: Record<string, any> = {};

    if (action === 'status') {
      if (this.status !== this.selectedSubject.status) {
        payload['status'] = this.status;
      }
    } else {
      if (this.nameOfSubject !== this.selectedSubject.name) {
        payload['name'] = this.nameOfSubject;
      }

      if (this.subjectCode !== this.selectedSubject.code) {
        payload['code'] = this.subjectCode;
      }

      if (this.subjectDescription !== this.selectedSubject.description) {
        payload['description'] = this.subjectDescription;
      }
    }

    if (!Object.keys(payload).length) {
      this.flashService.failure('No changes detected');
      return;
    }

    this.isEditing.set(true);

    this.subjectService
      .editSubject(`${ApiEndpoints.url}/subject/${this.selectedSubject.id}`, payload)
      .subscribe({
        next: () => {
          this.isEditing.set(false);

          this.changeStatus.set(false);
          this.editSubject.set(false);
          this.status = '';

          this.flashService.success(
            action === 'status'
              ? 'Subject status updated successfully'
              : 'Subject updated successfully',
          );

          this.getSubjects();
        },

        error: (err) => {
          this.isEditing.set(false);

          this.flashService.failure(err?.error?.message ?? 'Unable to update Subject');
        },
      });
  }
}
